import type { Contact, EchoflicksVideoSession } from "../types";

/**
 * User-credit accounting resolvers (single source of truth, mirroring
 * attribution.ts). Data comes from the dashboard-indexer projection of the
 * bot's credit events:
 *
 * - echo.started stamps the balance snapshot + flat per-echo price;
 * - final_video.delivered stamps whether tokens were actually deducted
 *   (credits_charged) and what the echo cost (credits_cost, 0 = free mode);
 * - commerce.tokens_granted refreshes the contact's credit_balance.
 *
 * Echoes older than the projection carry none of these fields — resolvers
 * return "unknown" / null and the UI renders nothing rather than guessing.
 */

export type EchoCredits =
  | { kind: "free"; nominalCost: number | null }
  | { kind: "charged"; cost: number | null }
  | { kind: "unknown" };

const asNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

export const resolveEchoCredits = (
  record: Pick<
    EchoflicksVideoSession,
    "credits_cost" | "credits_charged" | "credits_video_cost"
  >,
): EchoCredits => {
  const cost = asNumber(record.credits_cost);
  const nominalCost = asNumber(record.credits_video_cost);
  if (cost != null) {
    return cost > 0 ? { kind: "charged", cost } : { kind: "free", nominalCost };
  }
  // A charged legacy echo without an echo.started price snapshot: report the
  // charge without inventing an amount.
  if (record.credits_charged === true) return { kind: "charged", cost: null };
  if (record.credits_charged === false) return { kind: "free", nominalCost };
  return { kind: "unknown" };
};

export type ContactCreditSummary = {
  balance: number;
  /** How many echoes the balance still buys at the last reported price. */
  echoesLeft: number | null;
  asOf: string | null;
};

export const contactCreditSummary = (
  contact:
    | Pick<
        Contact,
        "credit_balance" | "credit_balance_updated_at" | "credit_video_cost"
      >
    | null
    | undefined,
): ContactCreditSummary | null => {
  const balance = asNumber(contact?.credit_balance);
  if (balance == null) return null;
  const price = asNumber(contact?.credit_video_cost);
  return {
    balance,
    echoesLeft: price != null && price > 0 ? Math.floor(balance / price) : null,
    asOf: contact?.credit_balance_updated_at ?? null,
  };
};
